import { type Permission } from '~/types/permission';

export type PermissionType =
  | 'create'
  | 'read'
  | 'write'
  | 'delete'
  | 'member'
  | 'select'
  | 'prioritize';

export type PermissionPaths =
  | `${(typeof PERMISSION_ENTITIES)[number]}.${PermissionType}.${'_value' | 'grant'}`
  | 'permissionGrant';

export const PERMISSION_ENTITIES = [
  'company',
  'costCenter',
  'dlnApproval',
  'dlnBilling',
  'dlnIn',
  'dlnEsg',
  'invoice',
  'invoiceIn',
  'invoiceOut',
  'organisationalGroup',
  'site',
  'supplier',
  'user',
  'userGroup',
  'vehicle',
] as const;

type PermissionOptions = {
  hasCreate?: boolean;
  hasRead?: boolean;
  hasWrite?: boolean;
  hasDelete?: boolean;
  hasMember?: boolean;
};

const createPermissionStructure = ({
  hasCreate = true,
  hasRead = true,
  hasWrite = true,
  hasDelete = false,
  hasMember = false,
}: PermissionOptions = {}) => {
  const permissionStructure: Partial<
    Record<PermissionType, { _value: boolean; grant: boolean }>
  > = {};

  if (hasRead) {
    permissionStructure.read = {
      _value: false,
      grant: false,
    };
  }

  if (hasCreate) {
    permissionStructure.create = {
      _value: false,
      grant: false,
    };
  }

  if (hasWrite) {
    permissionStructure.write = {
      _value: false,
      grant: false,
    };
  }

  if (hasDelete) {
    permissionStructure.delete = {
      _value: false,
      grant: false,
    };
  }

  if (hasMember) {
    permissionStructure.member = {
      _value: false,
      grant: false,
    };
  }

  return permissionStructure;
};

export const PERMISSIONS_INITIAL = {
  COMPANY: createPermissionStructure(),
  COST_CENTER: createPermissionStructure(),
  DLN_APPROVAL: createPermissionStructure({ hasCreate: false, hasRead: false }),
  DLN_BILLING: createPermissionStructure({ hasCreate: false, hasRead: false }),
  DLN_ESG: createPermissionStructure({ hasCreate: false, hasWrite: false }),
  DLN_IN: createPermissionStructure({ hasCreate: false, hasWrite: false }),
  INVOICE: createPermissionStructure({ hasCreate: false }),
  INVOICE_IN: createPermissionStructure({ hasCreate: false, hasWrite: false }),
  INVOICE_OUT: createPermissionStructure({ hasCreate: false, hasWrite: false }),
  ORGANIZATIONAL_UNIT: createPermissionStructure({
    hasDelete: true,
    hasMember: true,
  }),
  SITE: createPermissionStructure(),
  SUPPLIER: {
    prioritize: {
      _value: false,
      grant: false,
    },
    select: {
      _value: false,
      grant: false,
    },
  },
  USER: createPermissionStructure(),
  USER_GROUP: createPermissionStructure({ hasDelete: true, hasMember: true }),
  VEHICLE: createPermissionStructure(),
} as const;

export const PERMISSIONS_AVAILABLE = {
  ACCESS_ESG_DELIVERY_NOTE: {
    BACKEND: ['dln_esg.read'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um ESG Lieferungen einsehen zu können.',
    WEBAPP: 'dlnEsg.read._value',
  },
  ACCESS_INCOMING_DELIVERY_NOTE: {
    BACKEND: ['dln_in.read'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um eingehende Lieferungen einsehen und signieren zu können.',
    WEBAPP: 'dlnIn.read._value',
  },
  ACCESS_INCOMING_INVOICE: {
    BACKEND: ['invoice_in.read'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um eingehende Rechnungen und die Ergebnisse der automatisierten Rechnungsprüfung einsehen zu können.',
    WEBAPP: 'invoiceIn.read._value',
  },
  ACCESS_OUTGOING_INVOICE: {
    BACKEND: ['invoice_out.read'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um ausgehende Rechnungen und die Ergebnisse der automatisierten Rechnungsprüfung einsehen zu können.',
    WEBAPP: 'invoiceOut.read._value',
  },
  CREATE_COMPANY: {
    BACKEND: ['company.create'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um Firmen anlegen zu können.',
    WEBAPP: 'company.create._value',
  },
  CREATE_COST_CENTER: {
    BACKEND: ['accounting_reference.create'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um Kostenstellen anlegen zu können.',
    WEBAPP: 'costCenter.create._value',
  },
  CREATE_ORGANISATIONAL_GROUP: {
    BACKEND: ['org_unit.create'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um Organisations-Gruppen anlegen zu können.',
    WEBAPP: 'organisationalGroup.create._value',
  },
  CREATE_SITE: {
    BACKEND: ['site.create'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um Standorte anlegen zu können.',
    WEBAPP: 'site.create._value',
  },
  CREATE_USER: {
    BACKEND: ['user.create'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um Benutzer anlegen zu können.',
    WEBAPP: 'user.create._value',
  },
  CREATE_USER_GROUP: {
    BACKEND: ['user_group.create'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um Benutzer-Gruppen anlegen zu können.',
    WEBAPP: 'userGroup.create._value',
  },
  CREATE_VEHICLE: {
    BACKEND: ['vehicle.create'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um Fahrzeuge anlegen zu können.',
    WEBAPP: 'vehicle.create._value',
  },
  DELETE_ORGANISATIONAL_GROUP: {
    BACKEND: ['org_unit.delete'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um Organisations-Gruppen löschen zu können.',
    WEBAPP: 'organisationalGroup.delete._value',
  },
  DELETE_USER_GROUP: {
    BACKEND: ['user_group.delete'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um Benutzer-Gruppen löschen zu können.',
    WEBAPP: 'userGroup.delete._value',
  },
  GRANT_ACCESS_ESG_DELIVERY_NOTE: {
    BACKEND: ['dln_esg.read.grant'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um die Berechtigung zum Einsehen von ESG Lieferungen vergeben zu können.',
    WEBAPP: 'dlnEsg.read.grant',
  },
  GRANT_ACCESS_INCOMING_DELIVERY_NOTE: {
    BACKEND: ['dln_in.read.grant'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um die Berechtigung zum Einsehen eingehender Lieferungen vergeben zu können.',
    WEBAPP: 'dlnIn.read.grant',
  },
  GRANT_ACCESS_INCOMING_INVOICE: {
    BACKEND: ['invoice_in.read.grant'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um die Berechtigung zum Einsehen eingehender Rechnungen vergeben zu können.',
    WEBAPP: 'invoiceIn.read.grant',
  },
  GRANT_ACCESS_OUTGOING_INVOICE: {
    BACKEND: ['invoice_out.read.grant'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um die Berechtigung zum Einsehen ausgehender Rechnungen vergeben zu können.',
    WEBAPP: 'invoiceOut.read.grant',
  },
  GRANT_CREATE_COMPANY: {
    BACKEND: ['company.create.grant'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um die Berechtigung zum Anlegen von Firmen vergeben zu können.',
    WEBAPP: 'company.create.grant',
  },
  GRANT_CREATE_COST_CENTER: {
    BACKEND: ['accounting_reference.create.grant'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um die Berechtigung zum Anlegen von Kostenstellen vergeben zu können.',
    WEBAPP: 'costCenter.create.grant',
  },
  GRANT_CREATE_ORGANISATIONAL_GROUP: {
    BACKEND: ['org_unit.create.grant'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um die Berechtigung zum Anlegen von Organisations-Gruppen vergeben zu können.',
    WEBAPP: 'organisationalGroup.create.grant',
  },
  GRANT_CREATE_SITE: {
    BACKEND: ['site.create.grant'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um die Berechtigung zum Anlegen von Standorten vergeben zu können.',
    WEBAPP: 'site.create.grant',
  },
  GRANT_CREATE_USER: {
    BACKEND: ['user.create.grant'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um die Berechtigung zum Anlegen von Benutzern vergeben zu können.',
    WEBAPP: 'user.create.grant',
  },
  GRANT_CREATE_USER_GROUP: {
    BACKEND: ['user_group.create.grant'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um die Berechtigung zum Anlegen von Benutzer-Gruppen vergeben zu können.',
    WEBAPP: 'userGroup.create.grant',
  },
  GRANT_CREATE_VEHICLE: {
    BACKEND: ['vehicle.create.grant'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um die Berechtigung zum Anlegen von Fahrzeugen vergeben zu können.',
    WEBAPP: 'vehicle.create.grant',
  },
  GRANT_DELETE_ORGANISATIONAL_GROUP: {
    BACKEND: ['org_unit.delete.grant'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um die Berechtigung zum Löschen von Organisations-Gruppen vergeben zu können.',
    WEBAPP: 'organisationalGroup.delete.grant',
  },
  GRANT_DELETE_USER_GROUP: {
    BACKEND: ['user_group.delete.grant'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um die Berechtigung zum Löschen von Benutzer-Gruppen vergeben zu können.',
    WEBAPP: 'userGroup.delete.grant',
  },
  GRANT_MEMBER_ORGANISATIONAL_GROUP: {
    BACKEND: ['org_unit.member.grant'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um die Berechtigung zum Hinzufügen von Stammdaten zu Organisations-Gruppen vergeben zu können.',
    WEBAPP: 'organisationalGroup.member.grant',
  },
  GRANT_MEMBER_USER_GROUP: {
    BACKEND: ['user_group.member.grant'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um die Berechtigung zum Hinzufügen von Benutzern zu Benutzer-Gruppen vergeben zu können.',
    WEBAPP: 'userGroup.member.grant',
  },
  GRANT_PERMISSION: {
    BACKEND: ['access_control.grant'],
    DESCRIPTION:
      'Ermöglicht es jede Rolle und jedes Recht an andere Benutzer zu vergeben - einschließlich der Rechte, selbst Rechte zu vergeben',
    WEBAPP: 'permissionGrant',
  },
  GRANT_PRIORITIZE_SUPPLIER: {
    BACKEND: ['supplier.prioritize.grant'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um die Berechtigung zur Priorisierung von Lieferanten-Anbindungen vergeben zu können.',
    WEBAPP: 'supplier.prioritize.grant',
  },
  GRANT_READ_COMPANY: {
    BACKEND: ['company.read.grant'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um die Berechtigung zum Einsehen von Firmen vergeben zu können.',
    WEBAPP: 'company.read.grant',
  },
  GRANT_READ_COST_CENTER: {
    BACKEND: ['accounting_reference.read.grant'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um die Berechtigung zum Einsehen von Kostenstellen vergeben zu können.',
    WEBAPP: 'costCenter.read.grant',
  },
  GRANT_READ_INVOICE: {
    BACKEND: ['invoice.read.grant'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um die Berechtigung zum Einsehen von Rechnungen vergeben zu können.',
    WEBAPP: 'invoice.read.grant',
  },
  GRANT_READ_ORGANISATIONAL_GROUP: {
    BACKEND: ['org_unit.read.grant'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um die Berechtigung zum Einsehen von Organisations-Gruppen vergeben zu können.',
    WEBAPP: 'organisationalGroup.read.grant',
  },
  GRANT_READ_SITE: {
    BACKEND: ['site.read.grant'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um die Berechtigung zum Einsehen von Standorten vergeben zu können.',
    WEBAPP: 'site.read.grant',
  },
  GRANT_READ_USER: {
    BACKEND: ['user.read.grant'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um die Berechtigung zum Einsehen von Benutzern vergeben zu können.',
    WEBAPP: 'user.read.grant',
  },
  GRANT_READ_USER_GROUP: {
    BACKEND: ['user_group.read.grant'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um die Berechtigung zum Einsehen von Benutzer-Gruppen vergeben zu können.',
    WEBAPP: 'userGroup.read.grant',
  },
  GRANT_READ_VEHICLE: {
    BACKEND: ['vehicle.read.grant'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um die Berechtigung zum Einsehen von Fahrzeugen vergeben zu können.',
    WEBAPP: 'vehicle.read.grant',
  },
  GRANT_SELECT_SUPPLIER: {
    BACKEND: ['supplier.select.grant'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um die Berechtigung zur Verwaltung von Lieferanten-Anfragen und Relevanz vergeben zu können.',
    WEBAPP: 'supplier.select.grant',
  },
  GRANT_WRITE_COMPANY: {
    BACKEND: ['company.write.grant'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um die Berechtigung zum Ändern von Firmen vergeben zu können.',
    WEBAPP: 'company.write.grant',
  },
  GRANT_WRITE_COST_CENTER: {
    BACKEND: ['accounting_reference.write.grant'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um die Berechtigung zum Ändern von Kostenstellen vergeben zu können.',
    WEBAPP: 'costCenter.write.grant',
  },
  GRANT_WRITE_DELIVERY_NOTE_APPROVAL: {
    BACKEND: ['dln_approval.write.grant'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um die Berechtigung zur Freigabe von Lieferscheinen vergeben zu können.',
    WEBAPP: 'dlnApproval.write.grant',
  },
  GRANT_WRITE_DELIVERY_NOTE_BILLING: {
    BACKEND: ['dln_billing.write.grant'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um die Berechtigung zur Abrechnung von Lieferscheinen vergeben zu können.',
    WEBAPP: 'dlnBilling.write.grant',
  },
  GRANT_WRITE_INVOICE: {
    BACKEND: ['invoice.write.grant'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um die Berechtigung zum Ändern von Rechnungen vergeben zu können.',
    WEBAPP: 'invoice.write.grant',
  },
  GRANT_WRITE_ORGANISATIONAL_GROUP: {
    BACKEND: ['org_unit.write.grant'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um die Berechtigung zum Ändern von Organisations-Gruppen vergeben zu können.',
    WEBAPP: 'organisationalGroup.write.grant',
  },
  GRANT_WRITE_SITE: {
    BACKEND: ['site.write.grant'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um die Berechtigung zum Ändern von Standorten vergeben zu können.',
    WEBAPP: 'site.write.grant',
  },
  GRANT_WRITE_USER: {
    BACKEND: ['user.write.grant'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um die Berechtigung zum Ändern von Benutzern vergeben zu können.',
    WEBAPP: 'user.write.grant',
  },
  GRANT_WRITE_USER_GROUP: {
    BACKEND: ['user_group.write.grant'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um die Berechtigung zum Ändern von Benutzer-Gruppen vergeben zu können.',
    WEBAPP: 'userGroup.write.grant',
  },
  GRANT_WRITE_VEHICLE: {
    BACKEND: ['vehicle.write.grant'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um die Berechtigung zum Ändern von Fahrzeugen vergeben zu können.',
    WEBAPP: 'vehicle.write.grant',
  },
  MEMBER_ORGANISATIONAL_GROUP: {
    BACKEND: ['org_unit.member'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um Stammdaten zu Organisations-Gruppen hinzufügen zu können.',
    WEBAPP: 'organisationalGroup.member._value',
  },
  MEMBER_USER_GROUP: {
    BACKEND: ['user_group.member'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um Benutzer zu Benutzer-Gruppen hinzufügen zu können.',
    WEBAPP: 'userGroup.member._value',
  },
  PRIORITIZE_SUPPLIER: {
    BACKEND: ['supplier.prioritize'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um die Anbindung von Lieferanten priorisieren zu können.',
    WEBAPP: 'supplier.prioritize._value',
  },
  READ_COMPANY: {
    BACKEND: ['company.read'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um Firmen einsehen zu können.',
    WEBAPP: 'company.read._value',
  },
  READ_COST_CENTER: {
    BACKEND: ['accounting_reference.read'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um Kostenstellen einsehen zu können (u.a. wenn einer Lieferung eine Kostenstelle zugewiesen wird, sobald diese signiert wird).',
    WEBAPP: 'costCenter.read._value',
  },
  READ_INVOICE: {
    BACKEND: ['invoice.read'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um Rechnungen einsehen zu können.',
    WEBAPP: 'invoice.read._value',
  },
  READ_ORGANISATIONAL_GROUP: {
    BACKEND: ['org_unit.read'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um Organisations-Gruppen einsehen zu können.',
    WEBAPP: 'organisationalGroup.read._value',
  },
  READ_SITE: {
    BACKEND: ['site.read'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um Standorte einsehen zu können (u.a. wenn einer Lieferung ein Standort zugewiesen wird, sobald diese signiert wird).',
    WEBAPP: 'site.read._value',
  },
  READ_USER: {
    BACKEND: ['user.read'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um Benutzer einsehen zu können.',
    WEBAPP: 'user.read._value',
  },
  READ_USER_GROUP: {
    BACKEND: ['user_group.read'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um Benutzer-Gruppen einsehen zu können.',
    WEBAPP: 'userGroup.read._value',
  },
  READ_VEHICLE: {
    BACKEND: ['vehicle.read'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um Fahrzeuge einsehen zu können.',
    WEBAPP: 'vehicle.read._value',
  },
  SELECT_SUPPLIER: {
    BACKEND: ['supplier.select'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um Lieferanten auswählen und deren Freischaltung anfragen zu können.',
    WEBAPP: 'supplier.select._value',
  },
  WRITE_COMPANY: {
    BACKEND: ['company.write'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um Firmen ändern zu können.',
    WEBAPP: 'company.write._value',
  },
  WRITE_COST_CENTER: {
    BACKEND: ['accounting_reference.write'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um Kostenstellen ändern zu können.',
    WEBAPP: 'costCenter.write._value',
  },
  WRITE_DELIVERY_NOTE_APPROVAL: {
    BACKEND: ['dln_approval.write'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um Lieferscheine freigeben zu können.',
    WEBAPP: 'dlnApproval.write._value',
  },
  WRITE_DELIVERY_NOTE_BILLING: {
    BACKEND: ['dln_billing.write'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um Lieferscheine abrechnen zu können.',
    WEBAPP: 'dlnBilling.write._value',
  },
  WRITE_INVOICE: {
    BACKEND: ['invoice.write'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um Rechnungen ändern zu können.',
    WEBAPP: 'invoice.write._value',
  },
  WRITE_ORGANISATIONAL_GROUP: {
    BACKEND: ['org_unit.write'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um Organisations-Gruppen ändern zu können.',
    WEBAPP: 'organisationalGroup.write._value',
  },
  WRITE_SITE: {
    BACKEND: ['site.write'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um Standorte ändern zu können.',
    WEBAPP: 'site.write._value',
  },
  WRITE_USER: {
    BACKEND: ['user.write'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um Benutzer ändern zu können.',
    WEBAPP: 'user.write._value',
  },
  WRITE_USER_GROUP: {
    BACKEND: ['user_group.write'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um Benutzer-Gruppen ändern zu können.',
    WEBAPP: 'userGroup.write._value',
  },
  WRITE_VEHICLE: {
    BACKEND: ['vehicle.write'],
    DESCRIPTION:
      'Diese Berechtigung wird benötigt, um Fahrzeuge ändern zu können.',
    WEBAPP: 'vehicle.write._value',
  },
} as const satisfies Record<
  string,
  { BACKEND: Permission[]; DESCRIPTION: string; WEBAPP: PermissionPaths }
>;

export const DEFAULT_ROLE: Record<
  string,
  {
    DESCRIPTION: string;
    NAME: string;
    PERMISSIONS: Array<PermissionPaths | 'permission.grant'>;
  }
> = {
  ACCOUNT_ADMIN: {
    DESCRIPTION:
      'Die Account-Administrator Rolle wird üblicherweise einem Unternehmens-Account zugewiesen und' +
      'enthält alle verfügbaren Berechtigungen. Ein Account-Administrator kann somit alle Lieferungen und' +
      'Rechnungen einsehen sowie alle Stammdaten administrieren. Diese Rolle sollte nur an Benutzer vergeben' +
      'werden, welche die umfassenden Berechtigungen zur Administration der VESTIGAS Anwendung benötigen (z. B. IT-Admin).',
    NAME: 'Account-Administrator',
    PERMISSIONS: [
      'company.create._value',
      'company.read._value',
      'company.write._value',
      'costCenter.create._value',
      'costCenter.read._value',
      'costCenter.write._value',
      'dlnIn.read._value',
      'dlnEsg.read._value',
      'invoice.read._value',
      'invoice.write._value',
      'invoiceIn.read._value',
      'invoiceOut.read._value',
      'organisationalGroup.create._value',
      'organisationalGroup.delete._value',
      'organisationalGroup.member._value',
      'organisationalGroup.read._value',
      'organisationalGroup.write._value',
      'permission.grant',
      'site.create._value',
      'site.read._value',
      'site.write._value',
      'supplier.prioritize._value',
      'supplier.select._value',
      'user.create._value',
      'user.read._value',
      'user.write._value',
      'userGroup.create._value',
      'userGroup.delete._value',
      'userGroup.member._value',
      'userGroup.read._value',
      'userGroup.write._value',
      'vehicle.create._value',
      'vehicle.read._value',
      'vehicle.write._value',
    ],
  },
  CONSTRUCTION_MANAGER: {
    DESCRIPTION:
      'Die Rolle der Bauleitung wird üblicherweise einer Firma oder einem Standort zugewiesen. Ein' +
      'Benutzer mit dieser Rolle kann Lieferungen und eingehende Rechnungen einsehen. Zudem können Standorte' +
      'und Kostenstellen eingesehen und angelegt werden. Diese Rolle sollte an Benutzer vergeben werden, welche' +
      'auf Abnehmer Seite vollen Zugriff auf Standorte sowie deren Materialflüsse und Kostenstellen benötigen.',
    NAME: 'Bauleitung',
    PERMISSIONS: [
      'costCenter.create._value',
      'costCenter.read._value',
      'dlnIn.read._value',
      'invoice.read._value',
      'invoice.write._value',
      'invoiceIn.read._value',
      'site.create._value',
      'site.read._value',
    ],
  },
  DRIVER: {
    DESCRIPTION:
      'Ein Fahrer kann transportierte Lieferungen einsehen. Diese Rolle sollte an Benutzer vergeben' +
      'werden, welche Lieferungen transportieren und übergeben.',
    NAME: 'Fahrer',
    PERMISSIONS: ['dlnIn.read._value', 'vehicle.read._value'],
  },
  EMPLOYEE: {
    DESCRIPTION:
      'Die Mitarbeiter Rolle wird üblicherweise einem Unternehmens-Account zugewiesen. Ein Benutzer mit' +
      'dieser Rolle kann Standorte und Kostenstelle einsehen und anlegen.',
    NAME: 'Mitarbeiter',
    PERMISSIONS: [
      'costCenter.create._value',
      'costCenter.read._value',
      'site.create._value',
      'site.read._value',
      'user.read._value',
    ],
  },
  FOREMAN: {
    DESCRIPTION:
      'Die Polier/Lagerist Rolle wird üblicherweise einem Standort zugewiesen. Diese Rolle ermöglicht es einem' +
      'Benutzer, Lieferungen einzusehen, entgegenzunehmen und die angenommenen Lieferungen dem entsprechenden' +
      'Standort und einer Kostenstelle zuzuweisen.',
    NAME: 'Polier/Lagerist',
    PERMISSIONS: [
      'costCenter.create._value',
      'costCenter.read._value',
      'dlnIn.read._value',
      'site.create._value',
      'site.read._value',
    ],
  },
  GOODS_ISSUE: {
    DESCRIPTION:
      'Die Rolle der Warenausgabe wird üblicherweise einem Standort zugewiesen. Diese Rolle ermöglicht' +
      'es einem Benutzer, ausgehende Lieferungen einzusehen, auszugeben und die angenommenen Lieferungen dem' +
      'entsprechenden Standort und einer Kostenstelle zuzuweisen.',
    NAME: 'Warenausgabe',
    PERMISSIONS: ['dlnIn.read._value'],
  },
  INVOICE_CHECKING: {
    DESCRIPTION:
      'Die Rolle der Rechnungsprüfung wird üblicherweise einer Firma zugewiesen. Ein Benutzer mit dieser' +
      'Rolle kann eingehende Lieferungen und Rechnungen einsehen. Zudem können Standorte und Kostenstellen dieser' +
      'Firma eingesehen und angelegt werden. Diese Rolle sollte an Benutzer vergeben werden, welche auf Abnehmer' +
      'Seite einen Überblick über eingehende Lieferungen benötigen und im Prozess der Rechnungsprüfung beteiligt sind.',
    NAME: 'Rechnungsprüfung',
    PERMISSIONS: [
      'costCenter.create._value',
      'costCenter.read._value',
      'dlnIn.read._value',
      'invoice.read._value',
      'invoice.write._value',
      'invoiceIn.read._value',
      'site.create._value',
      'site.read._value',
    ],
  },
} as const;

export const INDIVIDUAL_ROLE = 'Individuell';
